<template>
	<div>
		<app-app-header v-model="indexStore.isHeaderVisible"
						:title="sport.title"
						:breadcrumbs="[{text: $t('label.sports_directory'), to: $app('sports')}]"
						:heading="false"
						:color="$config.public.colors.blog">

			<client-only>
				<btn v-if="authPermissions.update" fab :to="`https://admin.spoferan.com/sports/${sport._key}/edit`" class="ml-12 sm:ml-16">
					<icon :icon="icons.edit"/>
				</btn>
			</client-only>
			<btn fab @click="indexStore.share({id: `sport-${sport._key}`})" class="ml-12 sm:ml-16">
				<icon :icon="icons.share"/>
			</btn>

		</app-app-header>

		<app-content>
			<page-content xs-full sm-full background>
				<lazy-cdn-background-image v-if="sport.background_image && $device.isDesktop" class="fixed w-screen h-screen top-0 left-0 right-0 bottom-0"
										   :src="sport.background_image"
										   :ratio="sport.background_image.ratio" :width="1920" cover immediate/>

				<container tight xs-full sm-full>
					<card xs-tile sm-tile :style="{borderTop: `2px solid ${$config.public.colors.primary}`}">

						<parallax only-mobile>
							<cdn-image :src="sport.image" :alt="sport.title"
									   :ratio="16/9"
									   :size="{width: '100vw', md: {width: 760}}"
									   cover
									   gradient="to bottom, rgba(0,0,0,.0) 80%, rgba(0,0,0,.7)"
									   immediate class="sport-image">
								<div v-if="sport.image_credits" class="absolute left-0 bottom-0 mb-10 ml-16 sm:ml-24 md:ml-48">
									<a v-if="sport.image_credits_url" class="flex items-center" :href="sport.image_credits_url" target="_blank">
										<span class="text-3xs text-white mr-4">{{ $t('label.image_credits') }}: {{ sport.image_credits }}</span>
										<icon :icon="icons.external_link" xx-small color="white"/>
									</a>
									<span v-else class="text-3xs text-white">{{ $t('label.image_credits') }}: {{ sport.image_credits }}</span>
								</div>
							</cdn-image>
						</parallax>

						<div class="relative bg-white pt-24 pb-48 mt--8 sm:mt-0 px-16 sm:px-24 md:px-48 rounded-t-lg md:rounded-t-none">

							<div class="flex items-center justify-between mb-24">
								<h1 class="text-4xl sm:text-5xl md:text-6xl mb-0 sport-title">{{ sport.content_title }}</h1>

								<div v-if="sport._key === 'triathlon'" class="flex flex-column items-end">
									<span class="text-xs text-sub-text-color mb-4">{{ $t('label.presented_by') }}:</span>
									<nuxt-link :to="$web('events.show', 'triathlon-niederbayern')">
										<cdn-image src="/logos/triathlon-niederbayern.png" :size="{height: 56, ratio: 2822/2375}" alt="TRIATHLON NIEDERBAYERN"
												   title="TRIATHLON NIEDERBAYERN"/>
									</nuxt-link>
								</div>
							</div>

							<template v-if="sport.preview_text || sport.content">
								<p v-if="sport.preview_text" class="text-subtext-color leading-loose mb-16 sport-description">{{ sport.preview_text }}</p>

								<!-- Below Article Preview -->
								<qmn-ad v-if="$device.isMobile" :config="QmnAds.MobileHPATopAreaAd" class="my-24"/>
								<qmn-ad v-else :config="QmnAds.DesktopIntextAd" class="my-24"/>

								<template v-if="sport.content">
									<user-content :text="sport.content" large/>
								</template>
							</template>
							<missing-contact-card v-else flat location="sports_detail" :contact-placeholder="$t('text.sport_type.feedback_prefill', {sport: sport.title})" :text="$t('text.sport_type.missing_content', {sport: sport.title})"/>

							<divider margin="40"/>

							<section id="video-tips" >
								<h2 class="text-xl truncate mb-24">{{ $t('label.more_sport_tips_in_video') }}</h2>
								<watchbetter-embed playlist="Spoferan" autoplay/>
							</section>

							<!-- Below Article Content -->
							<qmn-ad v-if="$device.isMobile" :config="QmnAds.MobileHPABottomAreaAd" class="my-24"/>

							<template v-if="sport.clubs?.length">
								<divider margin="40"/>

								<section id="clubs" class="mb-32">
									<div class="flex items-center justify-between mb-12">
										<h2 class="text-xl truncate mb-0">{{ sport.title }}-{{ $t('label.clubs') }}</h2>
										<nuxt-link class="text-sm whitespace-no-wrap" :to="{path: $web('clubs.index'), query: {sport: sport._key}}">{{ $t('action.show_all') }}</nuxt-link>
									</div>
									<p class="text-sm leading-spaced w-full mb-24">{{ $t('text.sport_type.clubs', {sport: sport.title}) }}</p>

									<div class="mx--16 md:mx-0">
										<slider show-arrows visible-arrows full-width-spaced>
											<slider-item v-for="club in sport.clubs" :key="'club-suggest-' + club.id">
												<club-card :club="club" :to="$web('clubs.show', club._key)" class="w-200"/>
											</slider-item>
											<slider-item>
												<card class="w-200 h-180" :to="{path: $web('clubs.index'), query: sport.clubs.length ? {sport: sport._key} : null}">
													<div class="flex flex-column items-center justify-center h-full w-full">
														<icon :icon="icons.search" class="mb-8"/>
														<span class="text-sm text-center">{{ $t('action.show_clubs_in_environment') }}</span>
													</div>
												</card>
											</slider-item>
										</slider>
									</div>
								</section>
							</template>

							<template v-if="sport.events?.length">
								<divider margin="40"/>

								<section id="events" class="mb-32">
									<div class="flex items-center justify-between mb-12">
										<h2 class="text-xl truncate mb-0">{{ sport.title }}-{{ $t('label.competitions') }}</h2>
										<nuxt-link class="text-sm whitespace-no-wrap" :to="{path: $web('events.index'), query: {sport: sport._key}}">{{ $t('action.show_all') }}</nuxt-link>
									</div>
									<p class="text-sm leading-spaced w-full mb-24">{{ $t('text.sport_type.events', {sport: sport.title}) }}</p>

									<div class="mx--16 md:mx-0">
										<slider show-arrows visible-arrows full-width-spaced>
											<slider-item v-for="(event, eventIndex) in sport.events" :key="'event-suggest-' + event.id">
												<event-card :event="event" :to="$web('events.show', event._key)" :position="eventIndex + 1" list-name="Sports Page" class="w-240"/>
											</slider-item>
											<slider-item>
												<card class="w-240 h-240" :to="{path: $web('events.index'), query: sport.events.length ? {sport: sport._key} : null}">
													<div class="flex flex-column items-center justify-center h-full w-full">
														<icon :icon="icons.search" class="mb-8"/>
														<span class="text-sm text-center">{{ $t('action.show_events_in_environment') }}</span>
													</div>
												</card>
											</slider-item>
										</slider>
									</div>
								</section>
							</template>

							<template v-if="sport.articles?.length">
								<divider margin="40"/>

								<section id="articles" class="mb-32">
									<div class="flex items-center justify-between mb-12">
										<h2 class="text-xl truncate mb-0">{{ sport.title }}-{{ $t('label.news') }} {{ $t('conjunction.and') }} {{ $t('label.posts') }}</h2>
										<nuxt-link class="text-sm whitespace-no-wrap" :to="{path: $web('blog.index'), query: {sport: sport._key}}">{{ $t('action.show_all') }}</nuxt-link>
									</div>
									<p class="text-sm leading-spaced w-full mb-24">{{ $t('text.sport_type.articles', {sport: sport.title}) }}</p>

									<div class="mx--16 md:mx-0">
										<slider show-arrows visible-arrows full-width-spaced>
											<slider-item v-for="(article, articleIndex) in sport.articles" :key="'article-preview-' + articleIndex">
												<article-card :article="article" class="h-464 w-304"/>
											</slider-item>
											<slider-item>
												<card class="h-464 w-304" :to="{path: $web('blog.index'), query: sport.events.length ? {sport: sport._key} : null}">
													<div class="flex flex-column items-center justify-center h-full w-full">
														<icon :icon="icons.search" class="mb-8"/>
														<span class="text-sm text-center">{{ $t('action.show_all') }}</span>
													</div>
												</card>
											</slider-item>
										</slider>
									</div>

								</section>
							</template>

							<template v-if="sport.preview_text || sport.content">
								<divider margin="40"/>

								<section id="feedback" class="mb-32">
									<h2 class="text-xl truncate mb-24">{{ $t('question.got_feedback') }}</h2>
									<missing-contact-card location="sports_detail" flat :contact-placeholder="$t('text.sport_type.feedback_prefill', {sport: sport.title})" :text="$t('text.sport_type.feedback', {sport: sport.title})"/>
								</section>
							</template>

						</div>
					</card>

					<card xs-tile sm-tile>
						<qmn-ad v-if="$device.isMobile" :config="QmnAds.MobileProspektAd"/>
						<qmn-ad v-else :config="QmnAds.DesktopProspektAd"/>
					</card>

					<div class="none sm:block fixed mb-24 bottom-0 z-2" style="margin-left: 740px">
						<client-only>
							<tooltip v-if="authPermissions.update" :text="$t('action.edit')" left class="mb-8">
								<btn icon large shadow rounded-full :to="`https://admin.spoferan.com/sports/${sport._key}/edit`">
									<icon :icon="icons.edit"/>
								</btn>
							</tooltip>
						</client-only>
						<tooltip :text="$t('action.share')" left class="mb-8">
							<btn icon large shadow rounded-full @click="indexStore.share({id: `sport-${sport._key}`})">
								<icon :icon="icons.share"/>
							</btn>
						</tooltip>
					</div>

				</container>

			</page-content>

		</app-content>

		<template v-if="$device.isMobile">
			<qmn-ad :config="QmnAds.MobilePageTracker" class="none"/>
		</template>
		<template v-else>
			<qmn-ad :config="QmnAds.DesktopSitebarAd"/>
			<qmn-ad :config="QmnAds.DesktopPageTracker" class="none"/>
		</template>
	</div>
</template>

<script setup lang="ts">

import {useIndexStore} from "../../store";
import {ModelType} from "@spoferan/spoferan-ts-core";
import type {SportType} from "@spoferan/spoferan-ts-core";
import {editIcon, shareIcon, externalLinkIcon, searchIcon} from "@spoferan/nuxt-spoferan/icons";
import {QmnAds} from "../../config/qmn";

const {$api, $app, $auth, $image, $analytics} = useNuxtApp();
const config = useRuntimeConfig();
const route = useRoute();
const {t} = useI18n();
const {authPermissions, refreshAuthPermissions} = useModelAuthPermissions();
const {trackView} = useViewTracking();
const indexStore = useIndexStore();
const {value: cacheBusterValue} = useCacheBuster('sport_ts');

const {data} = await useApiFetch(`/v1/view/sports/${route.params.sport}`, {
	guest: true,
	params: {
		ts: cacheBusterValue
	}
});

const sport: SportType = data;

const icons = {
	search: searchIcon,
	edit: editIcon,
	share: shareIcon,
	external_link: externalLinkIcon
};

// Handle slug redirects
if (sport && sport._key !== route.params.sport) {
	await navigateTo({params: {sport: sport._key}}, {
		redirectCode: 308
	});
}

// Refresh permissions on user change as required when user gets authenticated on clients side after SSR hydration
watch(() => $auth.user, (user) => {
	if (user && user.is_admin) {
		refreshAuthPermissions(ModelType.SportType, sport.id);
	}
}, {
	immediate: true // Required for CSR
});

useMeta({
	title: sport.content_title,
	description: sport.preview_text,
	type: 'article',
	image: $image.format(sport.image),
	meta: [
		{
			'property': 'article:modified_time',
			'content': sport.updated_at
		},
		{
			'property': 'article:published_time',
			'content': sport.created_at
		},
		{
			'property': 'article:publisher',
			'content': 'https://www.facebook.com/spoferan'
		},
		{
			'property': 'article:section',
			'content': 'Sports'
		}
	],
	canonical: $app('sports-sport', {params: {sport: sport._key}}, {absolute: true})
});

useSchemaOrg([
	defineArticle({
		"@type": "BlogPosting",
		articleSection: "Sports",
		headline: sport.content_title,
		description: sport.preview_text,
		datePublished: sport.created_at,
		dateModified: sport.updated_at,
		image: $image.format(sport.image),
		mainEntityOfPage: $app('sports-sport', {params: {sport: sport._key}}, {absolute: true}),
	}),
])

onMounted(() => {
	trackView(ModelType.SportType, sport.id);
	$analytics.trackQmnPageImpression();
});

</script>

<style lang="scss">
// View Transition API
.sport-image img {
	view-transition-name: sport-image;
}

.sport-title {
	view-transition-name: sport-title;
}

.sport-description {
	view-transition-name: sport-description;
}

#ad-block-below-sport-article-preview {
	min-height: 166px;

	@include media-breakpoint-down('xs') {
		min-height: 345px;
	}
}
</style>